import { useEffect, useMemo, useState } from 'react';
import { Check, Save, Trash2 } from 'lucide-react';
import { Alert, Checkbox } from '../ui';
import {
  parseScopeSelection,
  serializeScopeSelection,
  sameScopeSelection,
  unknownScopeIds,
  reconcileScopeSelection,
} from '../../utils/axiomScope.js';

/** Above this many targets a filter field saves scrolling (tenant-wide tokens). */
const FILTER_THRESHOLD = 10;

/**
 * Pick the projects and collections Etch searches in Axiom, from the ones the
 * Axiom token actually reaches — by name, several at once.
 *
 * A token naming more than one target cannot search unscoped: every call is a
 * 400, and chat answers from model knowledge instead. This replaces the text
 * field that asked for UUIDs or JSON.
 *
 * @param {object} props
 * @param {string} props.targetsUrl  `/api/secrets/{me|tenant}/axiom-targets`
 * @param {string} props.saveUrl     `/api/secrets/{me|tenant}/axiom_scope`
 * @param {string} props.authToken
 * @param {string|null} props.storedValue  the scope in effect (setting.value)
 * @param {string|null} props.tokenFingerprint  changes when the Axiom token does
 * @param {boolean} props.canClear   whether this view owns the stored value
 * @param {() => void} props.onClear
 * @param {() => Promise<void>|void} props.onSaved
 * @param {() => void} props.onFallback  nyxCore cannot list targets — show free text
 */
export default function AxiomScopePicker({
  targetsUrl,
  saveUrl,
  authToken,
  storedValue,
  tokenFingerprint,
  canClear,
  onClear,
  onSaved,
  onFallback,
}) {
  const [targets, setTargets] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | no_token | invalid_token
  const [selection, setSelection] = useState(() => parseScopeSelection(storedValue));
  const [filter, setFilter] = useState('');
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState(null);

  const stored = useMemo(() => parseScopeSelection(storedValue), [storedValue]);
  useEffect(() => { setSelection(reconcileScopeSelection(stored, targets)); }, [stored, targets]);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    fetch(targetsUrl, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((res) => (res.ok ? res.json() : { available: false, reason: 'unavailable' }))
      .catch(() => ({ available: false, reason: 'unavailable' }))
      .then((data) => {
        if (cancelled) return;
        if (data.available) {
          setTargets(data);
          setStatus('ready');
        } else if (data.reason === 'no_token' || data.reason === 'invalid_token') {
          setStatus(data.reason);
        } else {
          onFallback();
        }
      });
    return () => { cancelled = true; };
    // onFallback is a parent callback; re-asking nyxCore when it changes identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetsUrl, authToken, tokenFingerprint]);

  if (status === 'loading') {
    return <p role="status" className="key-row-desc">Lade Projekte und Collections des Axiom-Tokens…</p>;
  }
  if (status === 'no_token') {
    return <p className="key-row-desc">Hinterlege zuerst einen Axiom-Token — danach erscheinen hier seine Projekte zur Auswahl.</p>;
  }
  if (status === 'invalid_token') {
    return <Alert tone="danger">nyxCore lehnt den Axiom-Token ab. Prüfe den Token oben.</Alert>;
  }

  const projectNames = Object.fromEntries(targets.projects.map((p) => [p.id, p.name]));
  const total = targets.projects.length + targets.collections.length;
  const needle = filter.trim().toLowerCase();
  const matches = (name) => !needle || name.toLowerCase().includes(needle);
  const empty = selection.projectIds.length === 0 && selection.collectionIds.length === 0;
  const unchanged = sameScopeSelection(selection, stored);
  const unknown = unknownScopeIds(stored, targets);

  const toggle = (kind, id) => setSelection((s) => ({
    ...s,
    [kind]: s[kind].includes(id) ? s[kind].filter((x) => x !== id) : [...s[kind], id],
  }));

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(saveUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ value: serializeScopeSelection(reconcileScopeSelection(selection, targets)) }),
      });
      if (!res.ok) throw new Error((await res.json()).error || 'Speichern fehlgeschlagen');
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
      await onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const docs = (n) => `${n} ${n === 1 ? 'Dokument' : 'Dokumente'}`;

  return (
    <div className="axiom-scope-picker">
      {targets.tenantWide ? (
        <p className="key-row-desc">
          Der Token gilt für den ganzen Mandanten. Ohne Auswahl durchsucht Etch alles — wähle aus, um die Suche einzugrenzen.
        </p>
      ) : total > 1 && stored.projectIds.length === 0 && stored.collectionIds.length === 0 ? (
        <Alert tone="warning">
          Dieser Token deckt {total} Ziele ab. Ohne Auswahl findet Axiom nichts, und Personas antworten nur aus Modellwissen.
        </Alert>
      ) : total === 1 ? (
        <p className="key-row-desc">Der Token nennt genau ein Ziel — Axiom sucht dort auch ohne Auswahl.</p>
      ) : null}

      {total > FILTER_THRESHOLD && (
        <input
          type="search"
          className="input"
          placeholder="Filtern…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Projekte und Collections filtern"
        />
      )}

      {targets.projects.length > 0 && (
        <fieldset className="axiom-scope-group">
          <legend>Projekte</legend>
          {targets.projects.filter((p) => matches(p.name)).map((p) => (
            <Checkbox
              key={p.id}
              label={p.name}
              hint={docs(p.documentCount)}
              checked={selection.projectIds.includes(p.id)}
              onChange={() => toggle('projectIds', p.id)}
              disabled={saving}
            />
          ))}
        </fieldset>
      )}

      {targets.collections.length > 0 && (
        <fieldset className="axiom-scope-group">
          <legend>Collections</legend>
          {targets.collections.filter((c) => matches(c.name)).map((c) => (
            <Checkbox
              key={c.id}
              label={c.name}
              hint={[
                c.projectId && projectNames[c.projectId] ? `in ${projectNames[c.projectId]}` : null,
                docs(c.documentCount),
              ].filter(Boolean).join(' · ')}
              checked={selection.collectionIds.includes(c.id)}
              onChange={() => toggle('collectionIds', c.id)}
              disabled={saving}
            />
          ))}
        </fieldset>
      )}

      {unknown.length > 0 && (
        <Alert tone="warning">
          Die gespeicherte Auswahl enthält {unknown.length === 1 ? 'ein Ziel' : `${unknown.length} Ziele`}, die dieser Zugang nicht (mehr) erreicht. Klicke auf „Auswahl speichern“, dann verschwindet dieser Hinweis.
        </Alert>
      )}

      {error && <div role="alert" className="admin-error">{error}</div>}

      <div className="key-row-input">
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={save}
          disabled={empty || unchanged || saving}
        >
          {justSaved ? (
            <>
              <Check size={14} aria-hidden="true" />
              <span>Gespeichert</span>
            </>
          ) : (
            <>
              <Save size={14} aria-hidden="true" />
              <span>{saving ? 'Speichere…' : 'Auswahl speichern'}</span>
            </>
          )}
        </button>
        {canClear && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClear}
            disabled={saving}
            title="Auswahl entfernen"
          >
            <Trash2 size={14} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
